/**
 * The canvas half of the editor: the part that needs a DOM.
 *
 * Everything decided here is arithmetic done elsewhere — `geometry.ts` says
 * where the pixels go, `adjustments.ts` says what colour they come out. This
 * file only owns the two things a browser has to do: draw, and encode.
 *
 * Rendering is split in two on purpose. Turning and cropping produces a *base*
 * that is expensive and changes rarely; adjusting colour is cheap and changes
 * on every frame of a slider drag. Keeping the base means a drag repaints from
 * a copy rather than re-drawing the source each time, which is the difference
 * between a slider that tracks the finger and one that lags behind it.
 */

import { trpcClient } from "../../lib/trpc.js";
import { acquireDecryptedUrl, release } from "../../lib/mediaCache.js";
import { type Adjustments, applyAdjustments } from "./adjustments.js";
import { type Transform, outputSize, renderMatrix } from "./geometry.js";

// Pure, and kept out of this file so a test can reach them without the DOM.
export { editedName, exportMime } from "./naming.js";

/**
 * Fetch a file and decode it, going through the same decryption path as
 * everything else on screen.
 *
 * The blob URL is released as soon as the frame is decoded: the decoded frame
 * is what is wanted, and holding both means holding the photograph twice.
 */
export async function loadBitmap(
  groupId: string,
  fileId: string,
  variant: "original" | "preview_hq" | "preview",
  contentType?: string,
): Promise<ImageBitmap> {
  const result = await trpcClient.media.url.query({
    groupId,
    fileId,
    variant,
    // Asked for as something to look at. An original requested any other way
    // comes back as an attachment, which `fetch` will happily hand over but
    // which is the wrong intent to be claiming.
    intent: "view",
  });
  if (!result.url)
    throw new Error("That version of this photo is not available.");

  const objectUrl = await acquireDecryptedUrl({
    url: result.url,
    wrappedFileKey: result.wrappedFileKey ?? null,
    groupId,
    contentType,
  });

  try {
    const response = await fetch(objectUrl);
    const blob = await response.blob();
    return await createImageBitmap(blob);
  } finally {
    release(objectUrl);
  }
}

/**
 * Draw the turned and cropped image, and hand back its pixels.
 *
 * `maxEdge` caps the working size for the on-screen preview: there is no point
 * pushing forty megapixels through a colour pass to fill an eight-hundred-pixel
 * box. Export passes no cap and gets the real thing.
 */
export function drawBase(
  canvas: HTMLCanvasElement,
  bitmap: ImageBitmap,
  transform: Transform,
  maxEdge?: number,
): ImageData {
  const full = outputSize(transform, bitmap.width, bitmap.height);

  // One scale factor for both axes, so nothing is stretched.
  const scale = maxEdge
    ? Math.min(1, maxEdge / Math.max(full.width, full.height))
    : 1;
  const width = Math.max(1, Math.round(full.width * scale));
  const height = Math.max(1, Math.round(full.height * scale));

  canvas.width = width;
  canvas.height = height;

  const context = canvas.getContext("2d", { willReadFrequently: true });
  if (!context) throw new Error("This browser cannot open the editor.");

  context.imageSmoothingQuality = "high";
  context.clearRect(0, 0, width, height);

  const matrix = renderMatrix(transform, bitmap.width, bitmap.height);
  context.setTransform(
    matrix.a * scale,
    matrix.b * scale,
    matrix.c * scale,
    matrix.d * scale,
    matrix.e * scale,
    matrix.f * scale,
  );
  context.drawImage(bitmap, 0, 0);
  context.setTransform(1, 0, 0, 1, 0, 0);

  return context.getImageData(0, 0, width, height);
}

/**
 * Repaint the canvas from a base, with colour applied.
 *
 * The base is never mutated — it is the thing every subsequent drag starts
 * from, and adjusting a copy of an already-adjusted image compounds rounding
 * until the photograph posterises.
 */
export function paintAdjusted(
  canvas: HTMLCanvasElement,
  base: ImageData,
  adjustments: Adjustments,
): void {
  const context = canvas.getContext("2d", { willReadFrequently: true });
  if (!context) return;

  const working = new ImageData(
    new Uint8ClampedArray(base.data),
    base.width,
    base.height,
  );
  applyAdjustments(working.data, adjustments);
  context.putImageData(working, 0, 0);
}

/** Render at full resolution, straight to a blob. Used only on save. */
export async function renderExport(
  bitmap: ImageBitmap,
  transform: Transform,
  adjustments: Adjustments,
  mime: string,
  quality = 0.92,
): Promise<Blob> {
  const canvas = document.createElement("canvas");
  const base = drawBase(canvas, bitmap, transform);
  paintAdjusted(canvas, base, adjustments);

  const blob = await new Promise<Blob | null>((resolve) => {
    canvas.toBlob(resolve, mime, quality);
  });
  if (!blob) throw new Error("The edited photo could not be encoded.");

  // Let the canvas go before the blob is handed on; at full resolution it is
  // the largest thing in the tab.
  canvas.width = 0;
  canvas.height = 0;

  return blob;
}
